import { FullPageSpinner } from 'components/FullPageSpinner'
import { Toaster } from 'components/Sonner'
import { ThemeProvider } from 'next-themes'
import { useMemo } from 'react'
import { BrowserRouter, useRoutes } from 'react-router-dom'
import { NavigationProvider } from 'services'
import { FacebookPixel } from './FacebookPixel'
import { useInit } from './hooks/use-init'
import { useKeyboard } from './hooks/use-keyboard'
import { routesGetter } from './modules'
import { selectIsHydrating, selectIsLoggedIn } from './modules/common'
import { useAppSelector } from './modules/core'
import { FeatureFlagProvider } from './modules/feature-flags'
import { OnboardingGuard } from './modules/onboarding'
function AppContent() {
  const isLoggedIn = useAppSelector(selectIsLoggedIn)
  const isHydrating = useAppSelector(selectIsHydrating)

  // Initialize app, authentication, and push notifications
  useInit()

  // Navigation testing hook
  useKeyboard()

  const routes = useMemo(() => routesGetter.get(isLoggedIn), [isLoggedIn])
  const routeElement = useRoutes(routes)

  // Hold all routing decisions until silent refresh resolves — otherwise the
  // authenticated route bucket is computed against `isLoggedIn=false` first, the
  // user is briefly bounced to /login, and the cookie-driven refresh swaps it
  // back a tick later. Visible flicker.
  if (isHydrating) return <FullPageSpinner />

  return <OnboardingGuard>{routeElement}</OnboardingGuard>
}

export const App = () => {
  return (
    // Theme provider sets `data-theme="dark|light"` on <html>. T3 tokens in
    // design-system.css flip on this attribute. Migrated surfaces (login,
    // onboarding, paywall, segway, plus everything Workstream 2+ touches)
    // toggle with the user's choice; legacy surfaces stay pinned at their
    // current white/purple look (they're not opted into dark via this
    // attribute and the legacy `.dark` class is intentionally not set
    // because most legacy components hardcode purple/whites that don't
    // flip cleanly).
    //
    // After the migration finishes (Workstream 8), the legacy CSS-vars
    // layer in index.css retires entirely and `data-theme` becomes the
    // single trigger for the whole app.
    //
    // - `defaultTheme="dark"` matches the T3 brand default.
    // - `enableSystem={false}` so we don't surprise users with a flash to
    //   light just because their OS is set that way; opting into light is
    //   an explicit user choice via the toggle in the user menu.
    // - `storageKey="taka-theme"` keeps the persisted theme namespaced.
    // - `disableTransitionOnChange` suppresses property-wide transitions
    //   during the swap so the theme flip is instant, not animated.
    <ThemeProvider
      attribute="data-theme"
      defaultTheme="dark"
      enableSystem={false}
      storageKey="taka-theme"
      disableTransitionOnChange
    >
      <BrowserRouter>
        <NavigationProvider>
          <FeatureFlagProvider>
            <FacebookPixel />
            <AppContent />
          </FeatureFlagProvider>
          <Toaster />
        </NavigationProvider>
      </BrowserRouter>
    </ThemeProvider>
  )
}
